import { SpaceData, SpacePluginData } from './types';
import { handleApiResponse } from '../common';
import { getApiBaseUrl, API_VERSION } from '../index';

/**
 * Spaces API client
 * Follows the same patterns as pluginApi and credentialApi
 */
export const spacesApi = {
  /**
   * List all spaces
   * @param {boolean} includeHidden - Whether to include hidden spaces (default: true)
   * @returns {Promise<SpaceData[]>}
   */
  async list(includeHidden = true) {
    try {
      const url = `${getApiBaseUrl()}${API_VERSION}/spaces?include_hidden=${includeHidden}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();

      if (!Array.isArray(data.spaces)) {
        console.error('Unexpected data format:', data);
        throw new Error('Invalid data format received from API');
      }

      return SpaceData.fromApiResponseList(data.spaces);
    } catch (error) {
      console.error('Failed to fetch spaces:', error);
      throw error;
    }
  },

  /**
   * Get a single space by ID
   * @param {number} spaceId
   * @returns {Promise<SpaceData>}
   */
  async get(spaceId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/spaces/${spaceId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      return SpaceData.fromApiResponse(data);
    } catch (error) {
      console.error(`Failed to fetch space ${spaceId}:`, error);
      throw error;
    }
  },

  /**
   * Create a new space
   * @param {Object} spaceData - Space creation data
   * @returns {Promise<SpaceData>}
   */
  async create(spaceData) {
    try {
      const payload = spaceData instanceof SpaceData 
        ? spaceData.toApiFormat() 
        : spaceData;

      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/spaces`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      return SpaceData.fromApiResponse(data);
    } catch (error) {
      console.error('Failed to create space:', error);
      throw error;
    }
  },

  /**
   * Update an existing space
   * @param {number} spaceId
   * @param {Object} updates - Partial space data to update
   * @returns {Promise<SpaceData>}
   */
  async update(spaceId, updates) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/spaces/${spaceId}`, {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(updates)
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      return SpaceData.fromApiResponse(data);
    } catch (error) {
      console.error(`Failed to update space ${spaceId}:`, error);
      throw error;
    }
  },

  /**
   * Delete a space
   * @param {number} spaceId
   * @returns {Promise<{message: string}>}
   */
  async delete(spaceId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/spaces/${spaceId}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const validatedResponse = await handleApiResponse(response);
      return await validatedResponse.json();
    } catch (error) {
      console.error(`Failed to delete space ${spaceId}:`, error);
      throw error;
    }
  },

  // =====================================================
  // Plugin Assignment Methods
  // =====================================================

  /**
   * Get all plugins assigned to a space
   * @param {number} spaceId
   * @returns {Promise<SpacePluginData[]>}
   */
  async getPlugins(spaceId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/spaces/${spaceId}/plugins`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      return SpacePluginData.fromApiResponseList(data.plugins || []);
    } catch (error) {
      console.error(`Failed to fetch plugins for space ${spaceId}:`, error);
      throw error;
    }
  },

  /**
   * Assign a plugin to a space
   * @param {number} spaceId
   * @param {number} pluginId
   * @param {number} displayOrder - Optional display order
   * @returns {Promise<{message: string}>}
   */
  async assignPlugin(spaceId, pluginId, displayOrder = 0) {
    try {
      const url = `${getApiBaseUrl()}${API_VERSION}/spaces/${spaceId}/plugins/${pluginId}?display_order=${displayOrder}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const validatedResponse = await handleApiResponse(response);
      return await validatedResponse.json();
    } catch (error) {
      console.error(`Failed to assign plugin ${pluginId} to space ${spaceId}:`, error);
      throw error;
    }
  },

  /**
   * Remove a plugin from a space
   * @param {number} spaceId
   * @param {number} pluginId
   * @returns {Promise<{message: string}>}
   */
  async removePlugin(spaceId, pluginId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/spaces/${spaceId}/plugins/${pluginId}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const validatedResponse = await handleApiResponse(response);
      return await validatedResponse.json();
    } catch (error) {
      console.error(`Failed to remove plugin ${pluginId} from space ${spaceId}:`, error);
      throw error;
    }
  },

  // =====================================================
  // Permission Methods
  // =====================================================

  /**
   * Get permissions for a space
   * @param {number} spaceId
   * @returns {Promise<Object>}
   */
  async getPermissions(spaceId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/spaces/${spaceId}/permissions`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const validatedResponse = await handleApiResponse(response);
      return await validatedResponse.json();
    } catch (error) {
      console.error(`Failed to fetch permissions for space ${spaceId}:`, error);
      throw error;
    }
  },

  /**
   * Grant a permission group access to a space
   * @param {number} spaceId
   * @param {number} groupId
   * @returns {Promise<Object>}
   */
  async grantAccess(spaceId, groupId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/spaces/${spaceId}/access`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ group_id: groupId })
      });

      const validatedResponse = await handleApiResponse(response);
      return await validatedResponse.json();
    } catch (error) {
      console.error(`Failed to grant access for space ${spaceId}:`, error);
      throw error;
    }
  },

  /**
   * Revoke a permission group's access to a space
   * @param {number} spaceId
   * @param {number} groupId
   * @returns {Promise<Object>}
   */
  async revokeAccess(spaceId, groupId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/spaces/${spaceId}/access/${groupId}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const validatedResponse = await handleApiResponse(response);
      return await validatedResponse.json();
    } catch (error) {
      console.error(`Failed to revoke access for space ${spaceId}:`, error);
      throw error;
    }
  },

  /**
   * Set user-specific access override
   * @param {number} spaceId
   * @param {number} userId
   * @param {boolean} granted
   * @returns {Promise<Object>}
   */
  async setUserAccess(spaceId, userId, granted) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/spaces/${spaceId}/user-access`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ user_id: userId, granted })
      });

      const validatedResponse = await handleApiResponse(response);
      return await validatedResponse.json();
    } catch (error) {
      console.error(`Failed to set user access for space ${spaceId}:`, error);
      throw error;
    }
  },

  /**
   * Remove user-specific access override
   * @param {number} spaceId
   * @param {number} userId
   * @returns {Promise<Object>}
   */
  async removeUserAccess(spaceId, userId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/spaces/${spaceId}/user-access/${userId}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const validatedResponse = await handleApiResponse(response);
      return await validatedResponse.json();
    } catch (error) {
      console.error(`Failed to remove user access for space ${spaceId}:`, error);
      throw error;
    }
  }
};









